import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "motion/react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  TrendingUp,
  Users,
  Clock,
  Search,
  MessageCircle,
  Phone,
  Mail,
  Linkedin,
  Eye,
  MousePointerClick,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Bookmark,
  Lightbulb,
  Bot,
  Activity,
} from "lucide-react";
import { ANALYTICS } from "@/lib/analytics";

export const Route = createFileRoute("/admin/growth")({
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const token =
      window.sessionStorage.getItem("publish-check:admin-token:v1") ?? "";
    if (!token) {
      window.location.replace("/admin/publish-check");
      throw new Error("redirecting");
    }
    try {
      const res = await fetch("/api/admin/verify", {
        headers: { "x-admin-token": token },
        cache: "no-store",
      });
      if (!res.ok) {
        window.sessionStorage.removeItem("publish-check:admin-token:v1");
        window.location.replace("/admin/publish-check");
        throw new Error("unauthorized");
      }
    } catch (e) {
      if ((e as Error).message === "unauthorized" || (e as Error).message === "redirecting") throw e;
    }
  },
  component: GrowthDashboard,
  head: () => ({
    meta: [
      { title: "Growth Intelligence Dashboard | Arif Khan" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "Private analytics dashboard." },
    ],
  }),
});

/* ---------- Demo data (replace with live GA4 / GSC pulls) ---------- */

const trafficSeries = [
  { d: "Mon", visitors: 412, returning: 134 },
  { d: "Tue", visitors: 538, returning: 178 },
  { d: "Wed", visitors: 612, returning: 201 },
  { d: "Thu", visitors: 588, returning: 192 },
  { d: "Fri", visitors: 724, returning: 248 },
  { d: "Sat", visitors: 612, returning: 220 },
  { d: "Sun", visitors: 690, returning: 260 },
];

const sourcesData = [
  { name: "Google Search", value: 42, color: "var(--color-primary)" },
  { name: "LinkedIn", value: 21, color: "#60a5fa" },
  { name: "Direct", value: 14, color: "#a78bfa" },
  { name: "Instagram", value: 8, color: "#f472b6" },
  { name: "X / Twitter", value: 5, color: "#94a3b8" },
  { name: "YouTube", value: 6, color: "#fb923c" },
  { name: "Referral", value: 4, color: "#22d3ee" },
];

const topContent = {
  articles: [
    { t: "How Perimeter Security Works at Scale", v: 4820 },
    { t: "Designing a Reference-Grade Home Cinema", v: 3915 },
    { t: "Smart Home Automation: The 2026 Stack", v: 3104 },
    { t: "AI in Surveillance — Beyond Buzzwords", v: 2701 },
  ],
  askArif: [
    { t: "What does perimeter intrusion detection cost?", v: 2210 },
    { t: "Dolby Atmos vs Auro-3D for private cinemas", v: 1840 },
    { t: "How long does a luxury cinema build take?", v: 1502 },
    { t: "PoE vs traditional cabling for automation", v: 1314 },
  ],
  videos: [
    { t: "60s — Perimeter design mistakes", v: 1820 },
    { t: "60s — Why cinema acoustics fail", v: 1640 },
    { t: "60s — Reading a security drawing", v: 1290 },
  ],
  projects: [
    { t: "Noida Industrial Perimeter", v: 1908 },
    { t: "Luxury Villa Cinema — Gurugram", v: 1742 },
    { t: "Auditorium AV — Education Campus", v: 1380 },
  ],
};

const searchQueries = [
  { q: "perimeter security india", imp: 12400, clk: 612, ctr: 4.9, pos: 6.2 },
  { q: "luxury home cinema new delhi", imp: 9800, clk: 480, ctr: 4.9, pos: 5.4 },
  { q: "smart home automation", imp: 18200, clk: 421, ctr: 2.3, pos: 9.8 },
  { q: "arif khan altima", imp: 3200, clk: 1840, ctr: 57.5, pos: 1.1 },
  { q: "auditorium av india", imp: 6100, clk: 224, ctr: 3.7, pos: 7.6 },
];

const locations = [
  { c: "New Delhi", v: 38 },
  { c: "Mumbai", v: 16 },
  { c: "Bengaluru", v: 12 },
  { c: "Hyderabad", v: 9 },
  { c: "Chennai", v: 7 },
  { c: "Other", v: 18 },
];

const deviceSplit = [
  { name: "Mobile", value: 61, color: "var(--color-primary)" },
  { name: "Desktop", value: 33, color: "#60a5fa" },
  { name: "Tablet", value: 6, color: "#a78bfa" },
];

const leads = {
  consultations: 24,
  whatsapp: 87,
  call: 41,
  email: 33,
  linkedin: 58,
  conversion: 3.4,
};

const knowledge = {
  mostRead: [
    "Perimeter detection systems explained",
    "Cinema room acoustic treatment 101",
    "When to choose KNX over wireless automation",
  ],
  mostShared: [
    "AI surveillance — what's real vs hype",
    "How to brief a security consultant",
    "Cost guide: luxury home cinema",
  ],
  trending: [
    "Drone defence for industrial perimeters",
    "Hidden costs of smart villas",
  ],
  mostSaved: [
    "Checklist: planning a private theatre",
    "RFP template for perimeter security",
  ],
};

const recommendations = [
  { title: "Publish: 'Cost guide — Perimeter security per km'", reason: "12k impressions, weak coverage" },
  { title: "Improve: /solutions/smart-automation hero", reason: "Bounce 71%, intent strong" },
  { title: "Internal link: Cinema → Auditorium AV", reason: "Topical cluster not connected" },
  { title: "New video: 'Reading a security drawing in 60s'", reason: "Top query, no video asset" },
];

/* ---------- Page ---------- */

function GrowthDashboard() {
  const liveConnections = [
    { label: "GA4", on: !!ANALYTICS.ga4 },
    { label: "GTM", on: !!ANALYTICS.gtm },
    { label: "Clarity", on: !!ANALYTICS.clarity },
    { label: "Search Console", on: false },
  ];

  return (
    <div className="min-h-screen bg-[oklch(0.14_0.02_250)] text-white">
      {/* subtle ambient */}
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(60% 40% at 50% 0%, color-mix(in oklab, hsl(var(--primary)) 18%, transparent), transparent), radial-gradient(40% 30% at 100% 100%, color-mix(in oklab, #60a5fa 14%, transparent), transparent)",
        }}
      />

      <header className="border-b border-white/10 backdrop-blur-md bg-black/20 sticky top-0 z-20">
        <div className="container-page flex items-center justify-between py-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
              <Activity className="h-4 w-4" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">
                Private · Arif Khan
              </div>
              <div className="text-sm font-medium">Growth Intelligence</div>
            </div>
          </div>
          <Link to="/" className="text-xs text-white/60 hover:text-white">
            ← Back to site
          </Link>
        </div>
      </header>

      <main className="container-page py-12 space-y-10">
        {/* Title */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-xs uppercase tracking-widest text-white/50">
              Growth Intelligence Dashboard
            </div>
            <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight">
              Insights from content, search and audience behavior.
            </h1>
            <p className="mt-3 text-white/60 max-w-2xl">
              Executive command center for digital authority growth — across Google, LinkedIn,
              YouTube, and AI search.
            </p>
          </motion.div>

          {/* Connection chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {liveConnections.map((c) => (
              <span
                key={c.label}
                className={[
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs",
                  c.on
                    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"
                    : "border-white/15 bg-white/5 text-white/60",
                ].join(" ")}
              >
                <span
                  className={[
                    "h-1.5 w-1.5 rounded-full",
                    c.on ? "bg-emerald-400" : "bg-white/30",
                  ].join(" ")}
                />
                {c.label} {c.on ? "live" : "not connected"}
              </span>
            ))}
          </div>
          {!ANALYTICS.ga4 && (
            <p className="mt-4 text-xs text-white/40 max-w-2xl">
              Demo data shown. Set <code className="text-white/70">VITE_GA4_MEASUREMENT_ID</code>,{" "}
              <code className="text-white/70">VITE_GTM_CONTAINER_ID</code>, and{" "}
              <code className="text-white/70">VITE_CLARITY_PROJECT_ID</code> in project secrets to
              activate live tracking. Search Console figures arrive via the Google Search Console
              connector.
            </p>
          )}
        </section>

        {/* TRAFFIC OVERVIEW */}
        <Section title="Traffic Overview" subtitle="Last 7 days">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat icon={<Users className="h-4 w-4" />} label="Total visitors" value="4,176" delta="+18.4%" up />
            <Stat icon={<Sparkles className="h-4 w-4" />} label="New visitors" value="2,742" delta="+12.1%" up />
            <Stat icon={<TrendingUp className="h-4 w-4" />} label="Returning" value="1,434" delta="+9.6%" up />
            <Stat icon={<TrendingUp className="h-4 w-4" />} label="Growth (4w)" value="+24%" delta="vs prior" up />
            <Stat icon={<Clock className="h-4 w-4" />} label="Avg. session" value="2m 41s" delta="+0:12" up />
          </div>
          <Card className="mt-4 p-6">
            <ChartFrame height={260}>
              <AreaChart data={trafficSeries}>
                <defs>
                  <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="d" tickLine={false} axisLine={false} stroke="rgba(255,255,255,0.4)" fontSize={11} />
                <YAxis tickLine={false} axisLine={false} stroke="rgba(255,255,255,0.4)" fontSize={11} width={28} />
                <Tooltip content={<DarkTooltip />} />
                <Area dataKey="visitors" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#gV)" />
                <Area dataKey="returning" stroke="#60a5fa" strokeWidth={2} fill="url(#gR)" />
              </AreaChart>
            </ChartFrame>
          </Card>
        </Section>

        {/* TRAFFIC SOURCES */}
        <Section title="Traffic Sources" subtitle="Share of sessions">
          <div className="grid lg:grid-cols-5 gap-4">
            <Card className="lg:col-span-2 p-6">
              <ChartFrame height={260}>
                <PieChart>
                  <Pie
                    data={sourcesData}
                    dataKey="value"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {sourcesData.map((s) => (
                      <Cell key={s.name} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DarkTooltip suffix="%" />} />
                </PieChart>
              </ChartFrame>
            </Card>
            <Card className="lg:col-span-3 p-6">
              <ul className="divide-y divide-white/5">
                {sourcesData.map((s) => (
                  <li key={s.name} className="flex items-center justify-between py-3">
                    <span className="flex items-center gap-3 text-sm">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                      {s.name}
                    </span>
                    <span className="flex items-center gap-4 text-sm">
                      <span className="text-white/50">{s.value}%</span>
                      <div className="h-1.5 w-32 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full"
                          style={{ width: `${s.value * 2}%`, background: s.color }}
                        />
                      </div>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </Section>

        {/* TOP CONTENT */}
        <Section title="Top Performing Content" subtitle="Last 28 days">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ListCard title="Top Articles" items={topContent.articles} unit="views" />
            <ListCard title="Top Ask Arif Topics" items={topContent.askArif} unit="reads" />
            <ListCard title="Top Videos" items={topContent.videos} unit="plays" />
            <ListCard title="Top Project Pages" items={topContent.projects} unit="views" />
          </div>
        </Section>

        {/* SEARCH INSIGHTS */}
        <Section title="Search Insights" subtitle="Google Search Console">
          <Card className="p-0 overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
              <div className="col-span-6">Query</div>
              <div className="col-span-2 text-right">Impr.</div>
              <div className="col-span-1 text-right">Clicks</div>
              <div className="col-span-1 text-right">CTR</div>
              <div className="col-span-2 text-right">Avg. position</div>
            </div>
            {searchQueries.map((q) => (
              <div
                key={q.q}
                className="grid grid-cols-12 px-6 py-3.5 items-center text-sm border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors"
              >
                <div className="col-span-6 flex items-center gap-2 truncate">
                  <Search className="h-3.5 w-3.5 text-white/40" />
                  <span className="truncate">{q.q}</span>
                </div>
                <div className="col-span-2 text-right tabular-nums text-white/70">
                  {q.imp.toLocaleString()}
                </div>
                <div className="col-span-1 text-right tabular-nums">{q.clk}</div>
                <div className="col-span-1 text-right tabular-nums text-white/70">{q.ctr}%</div>
                <div className="col-span-2 text-right tabular-nums">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-xs">
                    #{q.pos}
                  </span>
                </div>
              </div>
            ))}
          </Card>
        </Section>

        {/* AUDIENCE */}
        <Section title="Audience Insights" subtitle="Geo, device, engagement">
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="p-6">
              <CardTitle>Locations</CardTitle>
              <ChartFrame height={220}>
                <BarChart data={locations} layout="vertical">
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="c"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    stroke="rgba(255,255,255,0.6)"
                    fontSize={11}
                    width={80}
                  />
                  <Tooltip content={<DarkTooltip suffix="%" />} />
                  <Bar dataKey="v" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ChartFrame>
            </Card>
            <Card className="p-6">
              <CardTitle>Device split</CardTitle>
              <ChartFrame height={220}>
                <PieChart>
                  <Pie data={deviceSplit} dataKey="value" innerRadius={50} outerRadius={85} stroke="none">
                    {deviceSplit.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DarkTooltip suffix="%" />} />
                </PieChart>
              </ChartFrame>
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-white/60 justify-center">
                {deviceSplit.map((d) => (
                  <span key={d.name} className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
                    {d.name} · {d.value}%
                  </span>
                ))}
              </div>
            </Card>
            <Card className="p-6">
              <CardTitle>Time on page</CardTitle>
              <ChartFrame height={220}>
                <LineChart
                  data={[
                    { d: "Articles", t: 215 },
                    { d: "Ask Arif", t: 268 },
                    { d: "Solutions", t: 142 },
                    { d: "Projects", t: 196 },
                    { d: "About", t: 88 },
                  ]}
                >
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="d" tickLine={false} axisLine={false} stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <YAxis tickLine={false} axisLine={false} stroke="rgba(255,255,255,0.4)" fontSize={11} width={28} />
                  <Tooltip content={<DarkTooltip suffix="s" />} />
                  <Line dataKey="t" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ChartFrame>
            </Card>
          </div>
        </Section>

        {/* LEADS */}
        <Section title="Lead Dashboard" subtitle="Consultation engine">
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-4">
            <Stat icon={<Sparkles className="h-4 w-4" />} label="Consultations" value={String(leads.consultations)} delta="+6 this week" up />
            <Stat icon={<MessageCircle className="h-4 w-4" />} label="WhatsApp" value={String(leads.whatsapp)} delta="+22%" up />
            <Stat icon={<Phone className="h-4 w-4" />} label="Call clicks" value={String(leads.call)} delta="+8%" up />
            <Stat icon={<Mail className="h-4 w-4" />} label="Email" value={String(leads.email)} delta="−3%" />
            <Stat icon={<Linkedin className="h-4 w-4" />} label="LinkedIn" value={String(leads.linkedin)} delta="+14%" up />
            <Stat icon={<MousePointerClick className="h-4 w-4" />} label="Conv. rate" value={`${leads.conversion}%`} delta="+0.4 pts" up />
          </div>
        </Section>

        {/* KNOWLEDGE */}
        <Section title="Knowledge Performance" subtitle="Ask Arif & content engine">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SimpleList title="Most read" items={knowledge.mostRead} icon={<Eye className="h-3.5 w-3.5" />} />
            <SimpleList title="Most shared" items={knowledge.mostShared} icon={<ArrowUpRight className="h-3.5 w-3.5" />} />
            <SimpleList title="Trending" items={knowledge.trending} icon={<TrendingUp className="h-3.5 w-3.5" />} />
            <SimpleList title="Most saved" items={knowledge.mostSaved} icon={<Bookmark className="h-3.5 w-3.5" />} />
          </div>
        </Section>

        {/* AI VISIBILITY */}
        <Section
          title="AI Visibility"
          subtitle="Generative search & retrieval readiness"
          rightChip="Tracking soon"
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PlaceholderCard icon={<Bot className="h-4 w-4" />} title="AI mentions" hint="ChatGPT / Perplexity / Claude" />
            <PlaceholderCard icon={<TrendingUp className="h-4 w-4" />} title="AI referral traffic" hint="Sessions from AI clients" />
            <PlaceholderCard icon={<Search className="h-4 w-4" />} title="Citation opportunities" hint="Queries you could own" />
            <PlaceholderCard icon={<Sparkles className="h-4 w-4" />} title="Entity strength" hint="Knowledge graph signals" />
          </div>
        </Section>

        {/* RECOMMENDATIONS */}
        <Section title="Growth Recommendations" subtitle="Auto-suggested next moves">
          <div className="grid md:grid-cols-2 gap-4">
            {recommendations.map((r) => (
              <Card key={r.title} className="p-6 group hover:border-white/20 transition-colors">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 group-hover:bg-white/10 transition">
                    <Lightbulb className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{r.title}</div>
                    <div className="mt-1 text-xs text-white/50">{r.reason}</div>
                  </div>
                  <ArrowUpRight className="ml-auto h-4 w-4 text-white/30 group-hover:text-white transition" />
                </div>
              </Card>
            ))}
          </div>
        </Section>

        <footer className="pt-8 pb-4 text-xs text-white/30">
          Source: GA4 · Search Console · Clarity · internal events ·
          updated continuously. Demo values shown until live connectors are wired.
        </footer>
      </main>
    </div>
  );
}

/* ---------- Pieces ---------- */

function Section({
  title,
  subtitle,
  rightChip,
  children,
}: {
  title: string;
  subtitle?: string;
  rightChip?: string;
  children: React.ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  // simple in-view reveal
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onAnimationStart={() => setMounted(true)}
      data-mounted={mounted}
    >
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="text-xs text-white/50 mt-1">{subtitle}</p>}
        </div>
        {rightChip && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-widest text-white/60">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />
            {rightChip}
          </span>
        )}
      </div>
      {children}
    </motion.section>
  );
}

function Card({ className = "", children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={[
        "rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl",
        "shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{children}</div>
  );
}

function Stat({
  icon,
  label,
  value,
  delta,
  up,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  delta?: string;
  up?: boolean;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-white/70">
          {icon}
        </span>
        {delta && (
          <span
            className={[
              "inline-flex items-center gap-1 text-[11px]",
              up ? "text-emerald-300" : "text-white/50",
            ].join(" ")}
          >
            {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {delta}
          </span>
        )}
      </div>
      <div className="mt-4 text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-widest text-white/40">{label}</div>
    </Card>
  );
}

function ListCard({
  title,
  items,
  unit,
}: {
  title: string;
  items: { t: string; v: number }[];
  unit: string;
}) {
  const max = Math.max(...items.map((i) => i.v));
  return (
    <Card className="p-5">
      <CardTitle>{title}</CardTitle>
      <ul className="space-y-3">
        {items.map((i) => (
          <li key={i.t}>
            <div className="flex items-center justify-between text-sm">
              <span className="truncate pr-3">{i.t}</span>
              <span className="tabular-nums text-white/50 text-xs">
                {i.v.toLocaleString()} {unit}
              </span>
            </div>
            <div className="mt-1.5 h-1 w-full rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[hsl(var(--primary))] to-[#60a5fa]"
                style={{ width: `${(i.v / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SimpleList({
  title,
  items,
  icon,
}: {
  title: string;
  items: string[];
  icon: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <CardTitle>{title}</CardTitle>
      <ul className="space-y-2.5">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className="mt-1 text-white/40">{icon}</span>
            <span className="text-white/80">{i}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PlaceholderCard({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <Card className="p-5 relative overflow-hidden">
      <div
        className="pointer-events-none absolute -inset-px opacity-30"
        style={{
          background:
            "linear-gradient(135deg, transparent 0%, color-mix(in oklab, hsl(var(--primary)) 25%, transparent) 50%, transparent 100%)",
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-white/70">
            {icon}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-white/40">soon</span>
        </div>
        <div className="mt-4 text-sm font-medium">{title}</div>
        <div className="text-[11px] text-white/50 mt-1">{hint}</div>
        <div className="mt-4 h-1 w-full rounded-full bg-white/5 overflow-hidden">
          <div className="h-full w-1/3 bg-white/20" />
        </div>
      </div>
    </Card>
  );
}

function DarkTooltip({
  active,
  payload,
  label,
  suffix = "",
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number | string; color?: string; dataKey?: string }>;
  label?: string | number;
  suffix?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/80 backdrop-blur-md px-3 py-2 text-xs shadow-2xl">
      {label !== undefined && (
        <div className="text-white/50 uppercase tracking-widest text-[10px] mb-1">{label}</div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-white/70 capitalize">{p.name ?? p.dataKey}</span>
          <span className="ml-auto tabular-nums">
            {p.value}
            {suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChartFrame({ height, children }: { height: number; children: React.ReactElement }) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}
